import {
  LeadEventLifecycleStatus,
  LeadEventRosterMode,
  Permission,
  type LeadEventWithStatusDto,
} from '@up2stay/up2promo-api-client';
import { useQuery } from '@tanstack/react-query';

import { useEntitlements } from '@/core/hooks/use-entitlements';
import { usePermissions } from '@/core/hooks/use-permissions';
import { fetchAllLeadEventsPages } from '@/core/lib/lead-events-fetch-all';
import { leadEventsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { useAuthStore } from '@/core/store/auth.store';

/** Active events the current user would auto-attach to on capture. */
export function filterActiveCaptureEvents(
  events: LeadEventWithStatusDto[],
  userId: string | undefined,
): LeadEventWithStatusDto[] {
  const uid = userId?.trim();
  if (!uid) return [];
  return events.filter((ev) => {
    if (ev.status !== LeadEventLifecycleStatus.ACTIVE) return false;
    if (ev.rosterMode === LeadEventRosterMode.EVERYONE) return true;
    return (ev.rosterUserIds ?? []).includes(uid);
  });
}

export function useActiveCaptureEvents(
  workspaceId: string | undefined,
  enabled: boolean,
) {
  const userId = useAuthStore((s) => s.user?.id);
  const { isFeatureEnabled } = useEntitlements();
  const { can } = usePermissions();
  // Lead events need the plan feature and events:read. Without this gate,
  // opening the add-lead form on a plan without events 403s and the global
  // interceptor shows the upgrade toast.
  const eventsReadable =
    isFeatureEnabled('feature.lead_events') && can(Permission.EVENTS_READ);

  const query = useQuery({
    queryKey: workspaceId
      ? ([...leadEventsQueryKeys.all(workspaceId), 'capture'] as const)
      : leadEventsQueryKeys.root,
    queryFn: async () => {
      if (!workspaceId) return [];
      return fetchAllLeadEventsPages(workspaceId);
    },
    enabled: Boolean(workspaceId) && enabled && eventsReadable,
    staleTime: 30_000,
  });

  const candidates = filterActiveCaptureEvents(query.data ?? [], userId);

  return {
    ...query,
    candidates,
  };
}
